import * as vp from 'vopay';
import { ReactNode, createContext, useContext, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';

import usePostPortalPartnerLogin from '../hooks/useApi/usePostPortalPartnerLogin';
import usePostPartnerAuthPing from '../hooks/useApi/usePostPartnerAuthPing';
import usePartnerLoginDialogue from '../hooks/useDialogues/usePartnerLogin.dialogue';
import useStorage from '../hooks/useStorage';
import { useNavMenu } from './NavMenu.provider';
import { usePermissions } from './Permissions.provider';
import { usePartnerData } from './PartnerData.provider';
import { useDataDog } from './Datadog.provider';
import { useCache } from './Cache.provider';

import type { PortalPartnerLoginRequest } from '../hooks/useApi/usePostPortalPartnerLogin';

type PartnerLoginCtx = {
  // isAuthenticated is null if we haven't checked authentication yet. Once we have a response, we then set it to true or false.
  isAuthenticated: boolean | null;
  isSubmitting: boolean;
  response: {
    accountId: string;
    username: string;
    loginUsername: string;
    partnerType: vp.PartnerType | '';
    domain: string;
    userRole: vp.PartnerRole;
    isRoleAdmin: boolean;
    isRoleViewOnly: boolean;
    isInsideSales: boolean;
    success: boolean | undefined;
    MFAEnabled: boolean | undefined;
  };

  login: (requestParams: PortalPartnerLoginRequest) => void;
  logout: () => void;
  storeLogin: () => void;
  handleLoginUsername: (username: string) => void;
};

export const PartnerLoginContext = createContext<PartnerLoginCtx>({
  isAuthenticated: false,
  isSubmitting: false,
  response: {
    accountId: '',
    username: '',
    loginUsername: '',
    partnerType: '',
    domain: '',
    userRole: 'Admin',
    isRoleAdmin: false,
    isRoleViewOnly: false,
    isInsideSales: false,
    success: false,
    MFAEnabled: false,
  },

  login: () => {},
  logout: () => {},
  storeLogin: () => {},
  handleLoginUsername: () => {},
});

export const PartnerLoginProvider = ({ children }: { children: ReactNode }): React.JSX.Element => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loginPartnerResponse, loginPartner] = usePostPortalPartnerLogin({});
  const [tokenValidationResponse, validateTokens] = usePostPartnerAuthPing({});
  const [loginUsernameRequest, setLoginUsername] = useState('');
  const history = useHistory();
  const { reset: resetNavMenu } = useNavMenu();
  const { showDialogue } = usePartnerLoginDialogue(loginPartnerResponse);
  const { setPermissions, reset: resetPermissions } = usePermissions();
  const { clearCache } = useCache();
  const { setPartnerData, reset: clearPartnerData } = usePartnerData();
  const { setUser, clearUser } = useDataDog();
  const {
    login: { getPartnerLogin, storePartnerLogin, clearPartnerLogin, storeLoginUsername, storeLoginValues },
    permissions: { getPartnerPermissions, storePermissions, clearPermissions },
  } = useStorage();

  const { accountId, JWT, username, partnerType, partnerTypeLabel, loginUsername, domain, userRole, isInsideSales, success, MFAEnabled } =
    getPartnerLogin();
  const storedPermissions = getPartnerPermissions();

  useEffect(() => (storedPermissions ? setPermissions(storedPermissions) : resetPermissions()), [storedPermissions]);

  useEffect(() => {
    const handlers = {
      tokens: () => validateTokens(),
      noTokens: () => {
        setIsAuthenticated(false);
        history.push('/');
      },
    };

    return hasStoredLoginData() ? handlers.tokens() : handlers.noTokens();
  }, []);

  useEffect(() => {
    const handlers = {
      success: () => {
        setIsAuthenticated(true);
      },
      fail: () => {
        clearUser();
        clearPartnerLogin();
        setIsAuthenticated(false);
      },
    };

    if (tokenValidationResponse) return tokenValidationResponse.success ? handlers.success() : handlers.fail();

    return () => {};
  }, [tokenValidationResponse]);

  useEffect(() => {
    if (loginPartnerResponse) {
      storePermissions(loginPartnerResponse);
      storePartnerLogin(loginPartnerResponse, loginUsernameRequest);
      setUser({
        loginUsernameRequest,
        accountId: loginPartnerResponse.accountId,
        JWT: loginPartnerResponse.JWT,
        partnerType: loginPartnerResponse.partnerType,
      });
      setIsSubmitting(false);
    }

    if (loginPartnerResponse?.errorMessage) showDialogue();
  }, [loginPartnerResponse]);

  useEffect(() => {
    setPartnerData({
      accountId,
      username,
      loginUsername,
      partnerType,
      partnerTypeLabel: partnerTypeLabel ?? '',
      domain,
      JWT,
      userRole: userRole ?? '',
      isRoleAdmin: userRole === 'Admin',
      isRoleViewOnly: userRole === 'Viewer',
      isInsideSales: partnerType === 'Inside Sales',
    });
    if (accountId !== '') {
      setUser({
        loginUsernameRequest: username,
        accountId,

        partnerType,
      });
    }
  }, [accountId, username, partnerType, partnerTypeLabel, loginUsername, domain, userRole, isInsideSales, JWT]);

  useEffect(() => {
    if (loginPartnerResponse?.success && hasStoredLoginData()) {
      setIsAuthenticated(true);
      history.push('/dashboard');
    }
  }, [JWT]);

  useEffect(() => {
    if (JWT) {
      const timeout = setTimeout(
        () => {
          handleLogout();
        },
        24 * 60 * 60 * 1000 - 10 * 60 * 1000,
      ); // 10 minutes less than 24 hours

      return () => clearTimeout(timeout); // Cleanup timeout on unmount or dependency change
    }
    return () => {};
  }, [JWT]);

  const handleLogout = () => {
    clearPermissions();
    clearCache();
    clearPartnerLogin();
    resetNavMenu();
    clearPartnerData();
    setIsAuthenticated(false);
    clearUser();
    history.push('/');
  };

  const handleLoginUsername = (username: string) => {
    storeLoginUsername(username);
    setLoginUsername(username);
  };
  return (
    <PartnerLoginContext.Provider
      value={{
        isAuthenticated,
        isSubmitting,
        response: {
          accountId,
          username,
          loginUsername,
          partnerType,
          domain,
          userRole: userRole ?? 'Admin',
          isRoleAdmin: userRole === 'Admin',
          isRoleViewOnly: userRole === 'Viewer',
          isInsideSales: partnerType === 'Inside Sales',
          success,
          MFAEnabled,
        },
        login: (requestParams: PortalPartnerLoginRequest) => {
          setIsSubmitting(true);

          // save single var for later
          setLoginUsername(requestParams.username);
          loginPartner(requestParams);
        },
        logout: handleLogout,
        storeLogin: () => {
          if (loginPartnerResponse && loginPartnerResponse?.success) {
            storeLoginValues(loginPartnerResponse);
          }
        },
        handleLoginUsername,
      }}
    >
      {children}
    </PartnerLoginContext.Provider>
  );

  function hasStoredLoginData() {
    return [JWT].every((token) => Boolean(token));
  }
};

export const usePartnerLogin = (): PartnerLoginCtx => useContext(PartnerLoginContext);
